import { makeStyles, mergeClasses, tokens, Text } from '@fluentui/react-components';
import { CheckmarkFilled, WarningFilled, HandRightFilled } from '@fluentui/react-icons';
import { MOBILE_BREAKPOINT } from './constants';
import { HITL_BULLETS } from './data';
import { ottoTokens } from '@/theme/ottoTokens';

const useStyles = makeStyles({
  section: {
    paddingBlock: '100px',
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
    borderTopColor: tokens.colorNeutralStroke2,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      paddingBlock: '64px',
    },
  },
  inner: {
    maxWidth: '1200px',
    marginInline: 'auto',
    paddingInline: tokens.spacingHorizontalXXL,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'center',
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      gridTemplateColumns: '1fr',
      gap: '40px',
      paddingInline: tokens.spacingHorizontalL,
    },
  },
  // Visual on the left, copy on the right on desktop.
  reverseCopy: {
    [`@media (min-width: calc(${MOBILE_BREAKPOINT} + 1px))`]: {
      gridColumnStart: 2,
      gridRowStart: 1,
    },
  },
  reverseVisual: {
    [`@media (min-width: calc(${MOBILE_BREAKPOINT} + 1px))`]: {
      gridColumnStart: 1,
      gridRowStart: 1,
    },
  },
  eyebrow: {
    display: 'block',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: ottoTokens.landingAccentTextCool,
    marginBlockEnd: tokens.spacingVerticalM,
  },
  heading: {
    display: 'block',
    fontSize: 'clamp(28px, 4vw, 44px)',
    fontWeight: tokens.fontWeightBold,
    lineHeight: 1.1,
    letterSpacing: '-0.02em',
    color: tokens.colorNeutralForeground1,
    marginBlockEnd: tokens.spacingVerticalL,
  },
  lead: {
    fontSize: tokens.fontSizeBase400,
    lineHeight: 1.55,
    color: tokens.colorNeutralForeground2,
  },
  bullets: {
    listStyleType: 'none',
    paddingInlineStart: 0,
    marginBlockStart: tokens.spacingVerticalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  bullet: {
    fontSize: tokens.fontSizeBase300,
    lineHeight: 1.6,
    color: tokens.colorNeutralForeground2,
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
  },
  bulletCheck: {
    color: ottoTokens.statusSucceeded,
    fontSize: '18px',
    flexShrink: 0,
    marginBlockStart: '2px',
  },
  mockCard: {
    backgroundColor: ottoTokens.landingCardBackground,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    padding: '28px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  step: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
  },
  stepIdle: {
    opacity: 0.55,
  },
  icon: {
    width: '32px',
    height: '32px',
    borderRadius: tokens.borderRadiusMedium,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: '16px',
  },
  iconOk: {
    backgroundColor: ottoTokens.landingApprovalIconOk,
    color: ottoTokens.statusSucceeded,
  },
  iconWarn: {
    backgroundColor: ottoTokens.landingApprovalIconWarn,
    color: ottoTokens.statusWaiting,
  },
  iconIdle: {
    backgroundColor: ottoTokens.landingApprovalIconIdle,
    color: tokens.colorNeutralForeground3,
  },
  stepBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  stepTitle: {
    display: 'block',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  stepText: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: 1.5,
    color: tokens.colorNeutralForeground2,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
    marginBlockStart: tokens.spacingVerticalS,
  },
  actionBtn: {
    fontSize: tokens.fontSizeBase100,
    paddingBlock: '6px',
    paddingInline: '12px',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: ottoTokens.landingCardBackground,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  actionBtnPrimary: {
    backgroundColor: ottoTokens.statusSucceeded,
    color: tokens.colorNeutralForegroundInverted,
    border: `1px solid ${ottoTokens.statusSucceeded}`,
  },
});

export const HumanInLoopSection = () => {
  const styles = useStyles();

  return (
    <section className={styles.section} aria-labelledby="hitl-title">
      <div className={styles.inner}>
        <div className={styles.reverseCopy}>
          <Text className={styles.eyebrow}>Human in the loop</Text>
          <Text as="h2" id="hitl-title" className={styles.heading}>
            Stay in control. Always.
          </Text>
          <Text className={styles.lead}>
            AI shouldn&rsquo;t act blindly. Logic Apps Automation lets you choose exactly when
            humans need to step in, approve, reject, or escalate. Combine structured inputs and
            outputs with rule-based guardrails and approval processes. Include humans where they
            matter. Let AI handle the rest.
          </Text>
          <ul className={styles.bullets}>
            {HITL_BULLETS.map((b) => (
              <li key={b} className={styles.bullet}>
                <CheckmarkFilled className={styles.bulletCheck} aria-hidden="true" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className={mergeClasses(styles.mockCard, styles.reverseVisual)} aria-hidden="true">
          <div className={styles.step}>
            <span className={mergeClasses(styles.icon, styles.iconOk)}>
              <CheckmarkFilled />
            </span>
            <div className={styles.stepBody}>
              <Text className={styles.stepTitle}>Agent validated submission</Text>
              <Text className={styles.stepText}>187 of 200 rows passed automated checks.</Text>
            </div>
          </div>
          <div className={styles.step}>
            <span className={mergeClasses(styles.icon, styles.iconWarn)}>
              <WarningFilled />
            </span>
            <div className={styles.stepBody}>
              <Text className={styles.stepTitle}>13 rows flagged for review</Text>
              <Text className={styles.stepText}>
                Pricing variance exceeds 15% threshold. Awaiting human approval.
              </Text>
              <div className={styles.actions}>
                <span className={mergeClasses(styles.actionBtn, styles.actionBtnPrimary)}>
                  Approve all
                </span>
                <span className={styles.actionBtn}>Review each</span>
                <span className={styles.actionBtn}>Reject</span>
              </div>
            </div>
          </div>
          <div className={mergeClasses(styles.step, styles.stepIdle)}>
            <span className={mergeClasses(styles.icon, styles.iconIdle)}>
              <HandRightFilled />
            </span>
            <div className={styles.stepBody}>
              <Text className={styles.stepTitle}>Update inventory system</Text>
              <Text className={styles.stepText}>Waiting on approval above…</Text>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
